import { Box, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { DeleteIcon } from 'components/icons';
import type { OrganizationMember } from 'services/api';
import { formatPeopleDate } from '../formatPeopleDate';

const COL_NAME = 300;
const COL_CLASS = 160;
const COL_MAJOR = 220;
const COL_JOINED = 130;
const COL_LAST = 130;
const GAP = 32;

const TEXT_COLOR = '#383838';
const HEADER_BORDER = '#E5E7EB';
const ROW_BORDER = '#DEDEDE';
const COLOR_REMOVE = '#9B0000';

function HeaderCell ({ children, width }: { children: React.ReactNode; width: number }): ReactElement {
  return (
    <Box sx={{ width, flexShrink: 0, py: '16px', display: 'flex', alignItems: 'center' }}>
      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: '18px',
          textTransform: 'uppercase',
          color: TEXT_COLOR,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export function MemberRosterDesktopTable ({
  members,
  canRemove,
  onRemove,
}: {
  members: OrganizationMember[];
  canRemove: boolean;
  onRemove: (member: OrganizationMember) => void;
}): ReactElement {
  return (
    <Box sx={{ width: '100%', maxWidth: 1400, overflow: 'auto' }}>
      <Box
        display="flex"
        alignItems="center"
        gap={`${GAP}px`}
        sx={{ borderBottom: `1px solid ${HEADER_BORDER}` }}
      >
        <HeaderCell width={COL_NAME}>Name</HeaderCell>
        <HeaderCell width={COL_CLASS}>Classification</HeaderCell>
        <HeaderCell width={COL_MAJOR}>Major</HeaderCell>
        <HeaderCell width={COL_JOINED}>Date Joined</HeaderCell>
        <HeaderCell width={COL_LAST}>Last Attended</HeaderCell>
      </Box>

      {members.map(m => (
        <Box
          key={m.id}
          display="flex"
          alignItems="center"
          gap={`${GAP}px`}
          sx={{
            py: '16px',
            borderBottom: `1px solid ${ROW_BORDER}`,
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ width: COL_NAME, flexShrink: 0 }}>
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '24px',
                color: TEXT_COLOR,
              }}
            >
              {m.firstName} {m.lastName}
            </Typography>
            <Typography
              sx={{
                fontFamily: '\'Poppins\', sans-serif',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
                color: TEXT_COLOR,
              }}
            >
              {m.schoolEmail}
            </Typography>
          </Box>

          <Typography
            sx={{
              width: COL_CLASS,
              flexShrink: 0,
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '21px',
              color: TEXT_COLOR,
            }}
          >
            {m.classification || '—'}
          </Typography>

          <Typography
            sx={{
              width: COL_MAJOR,
              flexShrink: 0,
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '21px',
              color: TEXT_COLOR,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {m.major || '—'}
          </Typography>

          <Typography
            sx={{
              width: COL_JOINED,
              flexShrink: 0,
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '21px',
              color: TEXT_COLOR,
              whiteSpace: 'nowrap',
            }}
          >
            {formatPeopleDate(m.dateJoined)}
          </Typography>

          <Typography
            sx={{
              width: COL_LAST,
              flexShrink: 0,
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '21px',
              color: TEXT_COLOR,
              whiteSpace: 'nowrap',
            }}
          >
            {formatPeopleDate(m.lastAttended)}
          </Typography>

          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            {canRemove ? (
              <Box
                component="button"
                type="button"
                onClick={() => onRemove(m)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0,
                  border: `1px solid ${COLOR_REMOVE}`,
                  borderRadius: '10px',
                  backgroundColor: '#FFFFFF',
                  padding: '10px',
                  height: 43,
                  width: 101,
                  cursor: 'pointer',
                  color: COLOR_REMOVE,
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                  '&:hover': { backgroundColor: '#FAFAFA' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'inherit', '& svg': { width: 23, height: 23 } }}>
                  <DeleteIcon />
                </Box>
                <Typography component="span" sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' }}>
                  Remove
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
